/**
 * Cart Drawer — Horizon-pattern AJAX cart.
 * Shimmer effect on prices during updates.
 */
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, CartUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morph } from '@theme/morph';

/** Cadeaux produit par product_id (templates/product.cadeaux-json.liquid), cache 5 min. */
const giftCache = new Map();
const GIFT_TTL = 5 * 60 * 1000;

function getProductGifts(productId, itemUrl) {
  const hit = giftCache.get(productId);
  if (hit && Date.now() - hit.at < GIFT_TTL) return hit.promise;
  const url = `${(itemUrl || '').split('?')[0]}?view=cadeaux-json`;
  // Pas d'en-tête Accept JSON : Shopify renverrait le JSON standard du produit au lieu du template.
  const promise = fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => data.gifts || [])
    .catch(() => {
      // Échec (réseau, 429…) : pas de cache, on réessaiera au prochain changement de panier.
      giftCache.delete(productId);
      return [];
    });
  giftCache.set(productId, { at: Date.now(), promise });
  return promise;
}

class CartDrawerComponent extends DialogComponent {
  #updating = false;
  #syncingGift = false;
  /** File d'attente : une seule synchro cadeaux à la fois (deux synchros parallèles ajoutaient le kit deux fois). */
  #syncQueue = Promise.resolve();
  #drag = null;

  get sectionId() {
    return this.dataset.sectionId;
  }

  get giftVariantId() {
    return parseInt(this.dataset.giftVariantId, 10) || 0;
  }

  get giftThreshold() {
    return parseInt(this.dataset.giftThreshold, 10) || 0;
  }

  get giftExcludeDigital() {
    return this.dataset.giftExcludeDigital === 'true';
  }

  get giftAvailable() {
    return this.dataset.giftAvailable !== 'false';
  }

  get sampleProductIds() {
    return (this.dataset.sampleProductIds || '')
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter(Boolean);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartAdd, this.#onCartAdd);
    // Delegated on document: survives header re-renders.
    document.addEventListener('click', this.#onCartIconClick);
    // Drag-to-scroll for the upsell carousel (mouse only; touch keeps native scroll).
    this.addEventListener('pointerdown', this.#onDragStart);
    window.addEventListener('pointermove', this.#onDragMove, { passive: false });
    window.addEventListener('pointerup', this.#onDragEnd);
    // Close on link click: page swaps only replace #MainContent, the drawer lives outside it.
    this.addEventListener('click', this.#onDrawerLinkClick);

    // Landed here after being redirected off the disabled /cart page → open the drawer.
    const params = new URLSearchParams(window.location.search);
    if (params.has('open_cart')) {
      this.showDialog();
      // Auto-open: drop showModal()'s focus so no focus ring shows.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (active && this.refs.dialog?.contains(active)) active.blur();
        })
      );
      params.delete('open_cart');
      const query = params.toString();
      const clean = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartAdd, this.#onCartAdd);
    document.removeEventListener('click', this.#onCartIconClick);
    this.removeEventListener('pointerdown', this.#onDragStart);
    window.removeEventListener('pointermove', this.#onDragMove);
    window.removeEventListener('pointerup', this.#onDragEnd);
    this.removeEventListener('click', this.#onDrawerLinkClick);
  }

  // Close on in-drawer link click (skips new-tab / modified clicks).
  #onDrawerLinkClick = (event) => {
    const link = event.target.closest('a[href]');
    if (!link || link.target === '_blank') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (this.refs.dialog?.open) this.closeDialog();
  };

  #onDragStart = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const scroller = event.target.closest('.cart-upsell-scroll');
    if (!scroller) return;
    this.#drag = { scroller, startX: event.clientX, startScroll: scroller.scrollLeft, moved: false };
  };

  #onDragMove = (event) => {
    if (!this.#drag) return;
    const dx = event.clientX - this.#drag.startX;
    if (!this.#drag.moved && Math.abs(dx) > 4) {
      this.#drag.moved = true;
      this.#drag.scroller.classList.add('is-dragging');
    }
    if (this.#drag.moved) {
      this.#drag.scroller.scrollLeft = this.#drag.startScroll - dx;
      event.preventDefault();
    }
  };

  #onDragEnd = () => {
    if (!this.#drag) return;
    const { scroller, moved } = this.#drag;
    scroller.classList.remove('is-dragging');
    this.#drag = null;
    if (!moved) return;
    // Swallow the click fired right after a drag.
    const swallow = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };
    scroller.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => scroller.removeEventListener('click', swallow, true), 100);
  };

  #onCartIconClick = (event) => {
    const trigger = event.target.closest('.js-cart-toggle');
    if (!trigger) return;
    event.preventDefault();
    this.showDialog();
  };

  #onCartAdd = (event) => {
    const html = event.detail?.data?.sections?.[this.sectionId];
    const wasOpen = this.refs.dialog?.open;

    if (wasOpen) {
      this.#syncGiftSections().then((giftHTML) => {
        const next = giftHTML || html;
        if (next) this.#morphContent(next);
        this.#scrollToNewItem();
      });
      return;
    }

    // Closed drawer: open it and show the item now; only the progress bar waits for the slide-in.
    this.showDialog();
    if (html) this.#morphContent(html, 600);
    this.#scrollToNewItem();
    this.#syncGiftSections().then((giftHTML) => {
      if (giftHTML) this.#morphContent(giftHTML, 600);
    });
  };

  async showDialog() {
    await super.showDialog();
    this.#reinitSliders();
  }

  toggleShippingInfo(event) {
    event.stopPropagation();
    const tip = this.refs.shippingInfo;
    if (!tip) return;
    tip.hidden = !tip.hidden;
    event.target.closest?.('button')?.setAttribute('aria-expanded', String(!tip.hidden));
  }

  onDialogClick(event) {
    const rect = this.refs.dialog.getBoundingClientRect();
    const outside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;
    if (outside) this.closeDialog();
  }

  async updateQuantity(event) {
    const btn = event.target.closest('[data-line]');
    if (!btn || this.#updating) return;
    const line = parseInt(btn.dataset.line, 10);
    const newQty = Math.max(0, parseInt(btn.dataset.qty, 10));
    // Reaching 0 is a removal — animate it like the trash button.
    if (newQty === 0) {
      await this.#removeLine(line, btn.closest('.cart-item'), false);
      return;
    }
    // Optimistic: show the new quantity instantly so the stepper feels snappy.
    const display = this.querySelector(`[data-qty-display="${line}"]`);
    if (display) display.textContent = newQty;
    this.#shimmerLine(line);
    await this.#cartChange(line, newQty, btn.closest('.cart-item'));
  }

  async removeItem(event) {
    const btn = event.target.closest('[data-line]');
    if (!btn || this.#updating) return;
    await this.#removeLine(parseInt(btn.dataset.line, 10), btn.closest('.cart-item'), !!btn.dataset.gift);
  }

  /**
   * Lignes cadeaux (`_gift_for`) d'un produit, à mettre à jour dans la même requête que lui.
   * Retourne { rows, updates } pour la nouvelle quantité du produit (0 = retrait).
   */
  #linkedGiftUpdates(row, newQty) {
    const productId = row?.dataset.productId;
    if (!productId) return { rows: [], updates: {} };
    const parents = [...this.querySelectorAll(`.cart-item[data-product-id="${productId}"]`)];
    const oldTotal = parents.reduce((sum, r) => sum + (parseInt(r.dataset.qty, 10) || 0), 0);
    const newTotal = oldTotal - (parseInt(row.dataset.qty, 10) || 0) + newQty;
    const rows = [...this.querySelectorAll(`.cart-item[data-gift-for="${productId}"]`)];
    const updates = {};
    for (const giftRow of rows) {
      const giftQty = parseInt(giftRow.dataset.qty, 10) || 0;
      // Cadeau plafonné par le stock (qty < achetée) : on ne l'augmente pas ici, la synchro s'en charge.
      updates[giftRow.dataset.lineKey] = newTotal <= 0 ? 0 : giftQty === oldTotal ? newTotal : Math.min(giftQty, newTotal);
    }
    return { rows, updates };
  }

  // Collapse the line front-side (instant), then reconcile once it's gone.
  async #removeLine(line, row, isGift) {
    this.#updating = true;
    this.#shimmerTotal();
    // Declining the free gift stops it from being auto-added again.
    if (isGift) await this.#setGiftDeclined(true);

    // Cadeaux liés : retirés dans la même requête et repliés en même temps que le produit.
    const linked = this.#linkedGiftUpdates(row, 0);
    const collapseDone = Promise.all([
      row ? this.#collapseRow(row) : Promise.resolve(),
      ...linked.rows.map((giftRow) => this.#collapseRow(giftRow)),
    ]);

    try {
      const withGifts = row?.dataset.lineKey && linked.rows.length;
      const body = JSON.stringify({
        ...(withGifts
          ? { updates: { ...linked.updates, [row.dataset.lineKey]: 0 } }
          : { line, quantity: 0 }),
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });
      const url = withGifts ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;
      const res = await fetch(url, fetchConfig('json', { body }));
      const data = await res.json();
      if (data.errors || (withGifts && !res.ok)) {
        console.error('Cart error:', data.errors);
        return;
      }

      const giftHTML = await this.#syncGiftSections();
      const html = giftHTML || data.sections?.[this.sectionId];

      // Reconcile only after the collapse has finished so the swap is seamless.
      await collapseDone;
      if (html) this.#morphContent(html);
      if (!giftHTML) {
        document.dispatchEvent(new CartUpdateEvent(data, this.sectionId, {
          itemCount: data.item_count,
          sections: data.sections,
        }));
      }
    } catch (err) {
      console.error('Remove error:', err);
    } finally {
      this.#updating = false;
    }
  }

  // Animate a cart line collapsing vertically; resolves when done.
  #collapseRow(row) {
    return new Promise((resolve) => {
      const height = row.offsetHeight;
      row.style.height = `${height}px`;
      row.style.overflow = 'hidden';
      const ease = 'cubic-bezier(0.4, 0, 0.2, 1)';
      row.style.transition = `height 0.4s ${ease}, padding 0.4s ${ease}, margin 0.4s ${ease}, border-width 0.4s ${ease}, opacity 0.25s ease`;
      void row.offsetHeight; // commit start height
      // border-box: zero padding/border too so the row fully collapses.
      row.style.height = '0';
      row.style.paddingTop = '0';
      row.style.paddingBottom = '0';
      row.style.marginTop = '0';
      row.style.marginBottom = '0';
      row.style.borderBottomWidth = '0';
      row.style.opacity = '0';

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      row.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName === 'height') finish();
        },
        { once: true }
      );
      setTimeout(finish, 600); // fallback
    });
  }

  // Add a suggestion from the empty-cart state (the whole empty view is replaced).
  async addSuggestion(event) {
    const btn = event.target.closest('[data-variant-id]');
    if (!btn || this.#updating) return;
    this.#updating = true;

    const label = btn.querySelector('.cta-link__text') || btn;
    const originalLabel = label.textContent;
    label.textContent = btn.dataset.addingLabel || originalLabel;
    btn.classList.add('is-added');
    btn.disabled = true;

    try {
      const body = JSON.stringify({
        items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }],
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });
      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();
      if (!res.ok || data.errors) {
        label.textContent = originalLabel;
        btn.classList.remove('is-added');
        btn.disabled = false;
        return;
      }
      const giftHTML = await this.#syncGiftSections();
      const html = giftHTML || data.sections?.[this.sectionId];
      if (html) this.#morphContent(html);
      const fresh = await this.#fetchCart().catch(() => null);
      if (fresh) {
        document.dispatchEvent(new CartUpdateEvent(fresh, this.sectionId, { itemCount: fresh.item_count }));
      }
    } catch (err) {
      console.error('Add suggestion error:', err);
      label.textContent = originalLabel;
      btn.classList.remove('is-added');
      btn.disabled = false;
    } finally {
      this.#updating = false;
    }
  }

  async addGift(event) {
    const btn = event.target.closest('[data-variant-id]');
    if (!btn || this.#updating) return;
    this.#updating = true;

    const originalLabel = btn.textContent;
    // Instant "adding to cart…" state, muted (same as the upsell button).
    btn.textContent = btn.dataset.addingLabel || originalLabel;
    btn.classList.add('is-added');
    btn.disabled = true;
    this.#shimmerTotal();

    try {
      await this.#setGiftDeclined(false);
      const body = JSON.stringify({
        items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }],
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });
      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();
      if (data.sections?.[this.sectionId]) this.#morphContent(data.sections[this.sectionId]);
      this.#scrollToNewItem();
      const fresh = await this.#fetchCart().catch(() => null);
      if (fresh) {
        document.dispatchEvent(new CartUpdateEvent(fresh, this.sectionId, { itemCount: fresh.item_count }));
      }
    } catch (err) {
      console.error('Add gift error:', err);
      btn.textContent = originalLabel;
      btn.classList.remove('is-added');
      btn.disabled = false;
    } finally {
      this.#updating = false;
    }
  }

  async updateQuantitySelect(event) {
    const select = event.target.closest('[data-line]');
    if (!select || this.#updating) return;
    const line = parseInt(select.dataset.line, 10);
    this.#shimmerLine(line);
    await this.#cartChange(line, parseInt(select.value, 10), select.closest('.cart-item'));
  }

  async addFromRec(event) {
    const btn = event.target.closest('[data-variant-id]');
    if (!btn || this.#updating) return;
    this.#updating = true;

    const card = btn.closest('.cart-upsell-card');
    const label = btn.querySelector('.cta-link__text') || btn;
    const originalLabel = label.textContent;

    const revert = () => {
      label.textContent = originalLabel;
      btn.classList.remove('is-added');
      btn.disabled = false;
      if (card) {
        card.classList.remove('is-removing');
        card.style.marginRight = '';
      }
    };

    // Instant loading state; the card collapses in sync with the item appearing in the cart.
    label.textContent = btn.dataset.addingLabel || originalLabel;
    btn.classList.add('is-added');
    btn.disabled = true;
    this.#shimmerTotal();

    try {
      const body = JSON.stringify({
        items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }],
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.errors) {
        console.error('Cart add error:', data.errors || data.description);
        revert();
        return;
      }

      const giftHTML = await this.#syncGiftSections();
      const html = giftHTML || data.sections?.[this.sectionId];

      // Server's new upsell content, cross-faded in if this add empties the current gamme.
      let nextUpsellHTML = '';
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        nextUpsellHTML = (doc.querySelector('[data-cart-upsell]')?.innerHTML || '').trim();
      }

      // Collapse the card and reveal the item in the same frame.
      if (card) this.#collapseCard(card, nextUpsellHTML);
      if (html) this.#morphContent(html, 0, true);
      this.#scrollToNewItem();

      // CartUpdateEvent, not CartAddEvent: #onCartAdd would re-morph and break the collapse.
      const fresh = await this.#fetchCart().catch(() => null);
      if (fresh) {
        document.dispatchEvent(new CartUpdateEvent(fresh, this.sectionId, { itemCount: fresh.item_count }));
      }
    } catch (err) {
      console.error('Cart add error:', err);
      revert();
    } finally {
      this.#updating = false;
    }
  }

  // Collapse an upsell card by pulling the next cards over it, then remove it.
  #collapseCard(card, nextUpsellHTML = '') {
    const scroller = card.parentElement;
    // Last card of the current set:
    if (scroller && scroller.querySelectorAll('.cart-upsell-card').length <= 1) {
      if (nextUpsellHTML) {
        // Gamme exhausted but a fallback exists → cross-fade to it.
        this.#crossfadeUpsell(scroller, nextUpsellHTML);
      } else {
        // Nothing left → collapse the carousel vertically (then :empty hides it).
        this.#collapseRow(scroller).then(() => {
          scroller.innerHTML = '';
          scroller.removeAttribute('style');
        });
      }
      return;
    }

    // Card width + rendered gap: pulling the next cards by exactly this avoids an end jump.
    const next = card.nextElementSibling;
    let shift;
    if (next) {
      shift = next.getBoundingClientRect().left - card.getBoundingClientRect().left;
    } else {
      const gap = card.parentElement
        ? parseFloat(getComputedStyle(card.parentElement).columnGap) || 0
        : 0;
      shift = card.getBoundingClientRect().width + gap;
    }

    card.classList.add('is-removing');
    // Reflow to commit margin 0, then set the target so the transition fires now.
    void card.offsetWidth;
    card.style.marginRight = `-${shift}px`;

    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      card.removeEventListener('transitionend', onEnd);
      card.remove();
    };
    const onEnd = (event) => {
      if (event.propertyName === 'margin-right') remove();
    };
    card.addEventListener('transitionend', onEnd);
    setTimeout(remove, 1100); // fallback if transitionend doesn't fire
  }

  async #cartChange(line, quantity, row = null) {
    this.#updating = true;

    try {
      // Cadeaux liés : quantité ajustée dans la même requête que le produit.
      const linked = this.#linkedGiftUpdates(row, quantity);
      const withGifts = row?.dataset.lineKey && linked.rows.length;
      const body = JSON.stringify({
        ...(withGifts
          ? { updates: { ...linked.updates, [row.dataset.lineKey]: quantity } }
          : { line, quantity }),
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const url = withGifts ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;
      const res = await fetch(url, fetchConfig('json', { body }));
      const data = await res.json();

      if (data.errors || (withGifts && !res.ok)) {
        console.error('Cart error:', data.errors);
        return;
      }

      const giftHTML = await this.#syncGiftSections();
      if (giftHTML) {
        // Gift changed: #syncGiftSections already dispatched the fresh update.
        this.#morphContent(giftHTML);
      } else {
        if (data.sections?.[this.sectionId]) {
          this.#morphContent(data.sections[this.sectionId]);
        }
        document.dispatchEvent(new CartUpdateEvent(data, this.sectionId, {
          itemCount: data.item_count,
          sections: data.sections,
        }));
      }
    } catch (err) {
      console.error('Cart change error:', err);
    } finally {
      this.#updating = false;
    }
  }

  async #fetchCart() {
    const res = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  // Persist the declined gift as a cart attribute (survives reloads).
  async #setGiftDeclined(declined) {
    try {
      await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
        body: JSON.stringify({ attributes: { _gift_declined: declined ? 'true' : '' } }),
      }));
    } catch (err) {
      console.error('Gift decline update error:', err);
    }
  }

  /**
   * Free gift: add it at the threshold, remove it below. Returns section HTML to morph, or null.
   */
  #syncGiftSections() {
    const run = this.#syncQueue.then(() => this.#runGiftSync());
    this.#syncQueue = run.catch(() => null);
    return run;
  }

  async #runGiftSync() {
    // Strip rogue sample lines first so they never count towards the gift threshold.
    const strippedHTML = await this.#stripRogueSamples();
    const productGiftHTML = await this.#syncProductGifts();
    const giftHTML = await this.#syncGift();
    return giftHTML || productGiftHTML || strippedHTML;
  }

  /**
   * Cadeaux produit (custom.cadeau_avec_le_produit) : une ligne `_gift_for` par produit acheteur,
   * quantité = quantité achetée (dans la limite du stock), retirée avec son produit. Returns section HTML or null.
   */
  async #syncProductGifts() {
    if (this.#syncingGift) return null;

    let cart;
    try {
      cart = await this.#fetchCart();
    } catch {
      return null;
    }

    // Quantité achetée par produit (hors échantillons et cadeaux).
    const bought = new Map();
    for (const item of cart.items) {
      const props = item.properties || {};
      if (props._gift_for || props._sample || item.variant_id === this.giftVariantId) continue;
      const entry = bought.get(item.product_id) || { url: item.url, quantity: 0 };
      entry.quantity += item.quantity;
      bought.set(item.product_id, entry);
    }

    const desired = new Map();
    const used = new Map();
    for (const [productId, { url, quantity }] of bought) {
      const gifts = await getProductGifts(productId, url);
      for (const gift of gifts) {
        if (!gift.available) continue;
        let want = quantity;
        if (gift.max !== null) {
          want = Math.min(want, gift.max - (used.get(gift.variant_id) || 0));
        }
        if (want <= 0) continue;
        used.set(gift.variant_id, (used.get(gift.variant_id) || 0) + want);
        desired.set(`${productId}|${gift.variant_id}`, { productId, variantId: gift.variant_id, quantity: want });
      }
    }

    const updates = {};
    for (const item of cart.items) {
      const parent = item.properties && item.properties._gift_for;
      if (!parent) continue;
      const key = `${parent}|${item.variant_id}`;
      const want = desired.get(key)?.quantity || 0;
      if (item.quantity !== want) updates[item.key] = want;
      desired.delete(key);
    }
    const toAdd = [...desired.values()];
    if (!Object.keys(updates).length && !toAdd.length) return null;

    this.#syncingGift = true;
    try {
      let html = null;
      const sections = { sections: this.sectionId, sections_url: window.location.pathname };

      if (Object.keys(updates).length) {
        const res = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ updates, ...sections }) }));
        const data = await res.json();
        if (res.ok) html = data.sections?.[this.sectionId] || html;
      }

      // Un appel par cadeau : un cadeau refusé (stock) ne bloque pas les autres.
      for (const gift of toAdd) {
        const body = JSON.stringify({
          items: [{ id: gift.variantId, quantity: gift.quantity, properties: { _gift_for: String(gift.productId) } }],
          ...sections,
        });
        const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
        const data = await res.json();
        if (res.ok && !data.status) html = data.sections?.[this.sectionId] || html;
      }

      const fresh = await this.#fetchCart().catch(() => null);
      if (fresh) {
        document.dispatchEvent(
          new CartUpdateEvent(fresh, this.sectionId, { itemCount: fresh.item_count })
        );
      }
      return html;
    } catch (err) {
      console.error('Product gift sync error:', err);
      return null;
    } finally {
      this.#syncingGift = false;
    }
  }

  /**
   * Remove sample lines not added via the picker (no `_sample` property). Returns section HTML to morph, or null.
   */
  async #stripRogueSamples() {
    const sampleIds = this.sampleProductIds;
    if (!sampleIds.length || this.#syncingGift) return null;

    let cart;
    try {
      cart = await this.#fetchCart();
    } catch {
      return null;
    }

    const giftVariantId = this.giftVariantId;
    const updates = {};
    for (const item of cart.items) {
      if (item.variant_id === giftVariantId) continue;
      if (item.properties && (item.properties._sample || item.properties._gift_for)) continue;
      if (sampleIds.includes(item.product_id)) updates[item.key] = 0;
    }
    if (!Object.keys(updates).length) return null;

    this.#syncingGift = true;
    try {
      const body = JSON.stringify({
        updates,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });
      const res = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const data = await res.json();

      const fresh = await this.#fetchCart().catch(() => null);
      if (fresh) {
        document.dispatchEvent(
          new CartUpdateEvent(fresh, this.sectionId, { itemCount: fresh.item_count })
        );
      }
      return data.sections?.[this.sectionId] || null;
    } catch (err) {
      console.error('Sample strip error:', err);
      return null;
    } finally {
      this.#syncingGift = false;
    }
  }

  async #syncGift() {
    const variantId = this.giftVariantId;
    const threshold = this.giftThreshold;
    if (!variantId || !threshold || this.#syncingGift) return null;

    let cart;
    try {
      cart = await this.#fetchCart();
    } catch {
      return null;
    }

    const giftLine = cart.items.find((item) => item.variant_id === variantId && !item.properties?._gift_for);
    const excludeDigital = this.giftExcludeDigital;
    // Eligibility: subtotal excluding the gift (and, if enabled, non-shipping items).
    const base = cart.items.reduce((sum, item) => {
      if (item.variant_id === variantId) return sum;
      if (excludeDigital && item.requires_shipping === false) return sum;
      return sum + item.final_line_price;
    }, 0);
    const eligible = base >= threshold;
    const declined = cart.attributes && cart.attributes._gift_declined === 'true';

    let shouldAdd = false;
    let shouldRemove = false;
    let shouldFix = false;
    if (eligible) {
      // Add unless it's already there, the customer declined it, or it's out of stock.
      shouldAdd = !giftLine && !declined && this.giftAvailable;
      // Un seul kit par commande : ramène toute quantité > 1 à 1.
      shouldFix = !!giftLine && giftLine.quantity > 1;
    } else {
      // No longer eligible: reset the decline and pull the gift back out.
      if (declined) await this.#setGiftDeclined(false);
      shouldRemove = !!giftLine;
    }
    if (!shouldAdd && !shouldRemove && !shouldFix) return null;

    this.#syncingGift = true;
    try {
      // Collapse the auto-removed gift row like a manual removal.
      let giftCollapse = Promise.resolve();
      if (shouldRemove) {
        const giftRow = this.querySelector('[data-gift-row]');
        if (giftRow) giftCollapse = this.#collapseRow(giftRow);
      }

      const url = shouldAdd ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
      const payload = shouldAdd
        ? { items: [{ id: variantId, quantity: 1 }] }
        : { id: giftLine.key, quantity: shouldFix ? 1 : 0 };
      const body = JSON.stringify({
        ...payload,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(url, fetchConfig('json', { body }));
      const data = await res.json();

      // Refresh the cart bubble with the true post-change cart.
      const fresh = await this.#fetchCart().catch(() => null);
      if (fresh) {
        document.dispatchEvent(
          new CartUpdateEvent(fresh, this.sectionId, { itemCount: fresh.item_count })
        );
      }

      // Let the collapse finish before the caller morphs the body.
      await giftCollapse;

      return data.sections?.[this.sectionId] || null;
    } catch (err) {
      console.error('Gift sync error:', err);
      return null;
    } finally {
      this.#syncingGift = false;
    }
  }

  /**
   * Add shimmer effect on a specific line price + the total
   */
  #shimmerLine(line) {
    const linePrice = this.querySelector(`[data-line-price="${line}"]`);
    if (linePrice) linePrice.classList.add('shimmer-text');
    this.#shimmerTotal();
  }

  #shimmerTotal() {
    const total = this.querySelector('[data-cart-total]');
    if (total) total.classList.add('shimmer-text');
  }

  /**
   * @param {string} sectionHTML
   * @param {number} [barDelay] - ms to hold the progress bar before animating (drawer opening)
   * @param {boolean} [preserveUpsell] - keep the live upsell carousel (don't interrupt a collapse)
   */
  #morphContent(sectionHTML, barDelay = 0, preserveUpsell = false) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHTML, 'text/html');

    const newDialog = doc.querySelector('.cart-drawer');
    const currentDialog = this.querySelector('.cart-drawer');

    if (!newDialog || !currentDialog) return;

    // Cross-fade empty ↔ full (not while opening: the slide-in covers it).
    const wasEmpty = !currentDialog.querySelector('.cart-item');
    const willBeEmpty = !newDialog.querySelector('.cart-item');
    if (barDelay === 0 && wasEmpty !== willBeEmpty) {
      this.#crossfadeState(currentDialog, newDialog);
      return;
    }

    // Header + body: innerHTML swap (Swiper must be rebuilt from clean markup, not morphed).
    this.#swapRegion(currentDialog, newDialog, '[data-cart-header]');
    this.#swapRegion(currentDialog, newDialog, '[data-cart-body]');

    // Footer: morph so the progress bar's width transition animates.
    const curFooter = currentDialog.querySelector('[data-cart-footer]');
    const newFooter = newDialog.querySelector('[data-cart-footer]');
    // Server upsell markup, applied after the morph unless an add animation manages the carousel.
    const newUpsellHTML = newFooter
      ? newFooter.querySelector('[data-cart-upsell]')?.innerHTML
      : null;

    if (curFooter && newFooter) {
      if (barDelay > 0) {
        // Content updates now; the bar is released after the delay.
        this.#morphFooterWithBarDelay(curFooter, newFooter, barDelay);
      } else if (!this.#playTierComplete(curFooter, newFooter)) {
        // Tier just reached: #playTierComplete runs the celebration sequence.
        this.#morphFooter(curFooter, newFooter);
      }
    } else if (curFooter) {
      curFooter.remove();
    } else if (newFooter) {
      currentDialog.appendChild(newFooter.cloneNode(true));
    }

    // Refresh the upsell carousel, except during an add (addFromRec manages it).
    if (!preserveUpsell && newUpsellHTML != null) {
      const liveUpsell = currentDialog.querySelector('[data-cart-upsell]');
      if (liveUpsell) liveUpsell.innerHTML = newUpsellHTML;
    }

    this.#reinitSliders();
  }

  // Morph the footer without reconciling the live upsell carousel.
  #morphFooter(curFooter, newFooter) {
    const curUp = curFooter.querySelector('[data-cart-upsell]');
    const newUp = newFooter.querySelector('[data-cart-upsell]');
    if (curUp && newUp) {
      newUp.innerHTML = curUp.innerHTML;
      // Preserve the live inline style (e.g. an in-progress collapse).
      const style = curUp.getAttribute('style');
      if (style) newUp.setAttribute('style', style);
    }

    // Animate the rewards block's height change instead of snapping.
    const rewards = curFooter.querySelector('[data-cart-rewards]');
    const oldHeight = rewards ? rewards.offsetHeight : null;

    morph(curFooter, newFooter);

    const newRewards = curFooter.querySelector('[data-cart-rewards]');
    if (newRewards && oldHeight != null) this.#flipHeight(newRewards, oldHeight);
  }

  // FLIP a height change: start from the old height and transition to the new.
  #flipHeight(el, oldHeight) {
    const newHeight = el.offsetHeight;
    if (newHeight === oldHeight) return;

    el.style.overflow = 'hidden';
    el.style.height = `${oldHeight}px`;
    void el.offsetHeight; // commit the start height
    el.style.transition = 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
    el.style.height = `${newHeight}px`;

    let done = false;
    const clear = () => {
      if (done) return;
      done = true;
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
      el.removeEventListener('transitionend', onEnd);
    };
    const onEnd = (event) => {
      if (event.propertyName === 'height') clear();
    };
    el.addEventListener('transitionend', onEnd);
    setTimeout(clear, 600); // fallback
  }

  #morphFooterWithBarDelay(curFooter, newFooter, delay) {
    // Sync totals/checkout immediately.
    this.#copyText(newFooter, curFooter, '[data-cart-total-amount]');
    this.#copyText(newFooter, curFooter, '[data-cart-total]');

    // Tier just completed: play the celebration once the drawer has opened.
    const progress = curFooter.querySelector('[data-tier$="-progress"]');
    const tier = progress ? progress.dataset.tier.replace('-progress', '') : null;
    const willComplete = tier && newFooter.querySelector(`[data-tier="${tier}-done"]`);
    if (willComplete) {
      setTimeout(() => this.#playTierComplete(curFooter, newFooter), delay);
      return;
    }

    // Within-tier: freeze the bar, morph the rest, release it after the open.
    const oldBar = curFooter.querySelector('.cart-drawer__progress-bar');
    const oldWidth = oldBar ? oldBar.style.width : null;

    this.#morphFooter(curFooter, newFooter);

    const bar = curFooter.querySelector('.cart-drawer__progress-bar');
    if (!bar || oldWidth == null) return;

    const target = bar.style.width;
    if (target === oldWidth) return;

    bar.style.transition = 'none';
    bar.style.width = oldWidth;
    void bar.offsetWidth; // commit the old width
    setTimeout(() => {
      bar.style.transition = '';
      bar.style.width = target;
    }, delay);
  }

  #swapRegion(currentRoot, newRoot, selector) {
    const cur = currentRoot.querySelector(selector);
    const inc = newRoot.querySelector(selector);
    if (cur && inc) cur.innerHTML = inc.innerHTML;
  }

  /**
   * If a reward tier just completed, fill the bar to 100% then fade to the done state. Returns true if handled.
   */
  #playTierComplete(curFooter, newFooter) {
    const progress = curFooter.querySelector('[data-tier$="-progress"]');
    if (!progress) return false;
    const tier = progress.dataset.tier.replace('-progress', '');
    const willBeDone = newFooter.querySelector(`[data-tier="${tier}-done"]`);
    if (!willBeDone) return false;

    const bar = progress.querySelector('.cart-drawer__progress-bar');
    const remaining = progress.querySelector('[data-remaining]');

    // Keep the totals in sync immediately (they only morph at the end).
    this.#copyText(newFooter, curFooter, '[data-cart-total-amount]');
    this.#copyText(newFooter, curFooter, '[data-cart-total]');

    // 1) Fill the bar + flip the remaining amount to "Offert".
    if (remaining) remaining.textContent = remaining.dataset.offered || remaining.textContent;
    if (bar) bar.style.width = '100%';

    // 2) Fade the progress block out, swap to the done state, fade it in.
    const finish = () => {
      progress.style.transition = 'opacity 0.3s ease';
      progress.style.opacity = '0';
      setTimeout(() => {
        this.#morphFooter(curFooter, newFooter);
        const done = curFooter.querySelector(`[data-tier="${tier}-done"]`);
        if (done) {
          done.style.opacity = '0';
          done.style.transition = 'opacity 0.35s ease';
          requestAnimationFrame(() => { done.style.opacity = '1'; });
        }
      }, 300);
    };

    if (bar) {
      let ended = false;
      const onEnd = (e) => {
        if (e.propertyName !== 'width') return;
        ended = true;
        bar.removeEventListener('transitionend', onEnd);
        finish();
      };
      bar.addEventListener('transitionend', onEnd);
      // Fallback in case transitionend doesn't fire.
      setTimeout(() => {
        if (ended) return;
        bar.removeEventListener('transitionend', onEnd);
        finish();
      }, 1100);
    } else {
      finish();
    }

    return true;
  }

  #copyText(fromRoot, toRoot, selector) {
    const from = fromRoot.querySelector(selector);
    const to = toRoot.querySelector(selector);
    if (from && to) to.textContent = from.textContent;
  }

  #reinitSliders() {
    if (typeof window.initCvSliders !== 'function') return;
    this.querySelectorAll('.js-cv-slider.is-initialized').forEach((s) =>
      s.classList.remove('is-initialized')
    );
    requestAnimationFrame(() => window.initCvSliders());
  }

  // Reveal the new item only if it's below the fold.
  #scrollToNewItem() {
    const body = this.querySelector('[data-cart-body]');
    if (!body) return;
    requestAnimationFrame(() => {
      const items = body.querySelectorAll('.cart-item');
      const last = items[items.length - 1];
      if (!last) return;
      if (last.getBoundingClientRect().bottom > body.getBoundingClientRect().bottom + 1) {
        this.#smoothScrollTo(body, body.scrollHeight - body.clientHeight, 900);
      }
    });
  }

  // Cross-fade the empty ↔ full transition (body + footer) instead of a hard swap.
  #crossfadeState(currentDialog, newDialog) {
    const fadeOut = [
      currentDialog.querySelector('[data-cart-body]'),
      currentDialog.querySelector('[data-cart-footer]'),
    ].filter(Boolean);
    fadeOut.forEach((el) => {
      el.style.transition = 'opacity 0.2s ease';
      el.style.opacity = '0';
    });

    setTimeout(() => {
      this.#swapRegion(currentDialog, newDialog, '[data-cart-header]');
      this.#swapRegion(currentDialog, newDialog, '[data-cart-body]');

      const curFooter = currentDialog.querySelector('[data-cart-footer]');
      const newFooter = newDialog.querySelector('[data-cart-footer]');
      if (curFooter && newFooter) morph(curFooter, newFooter);
      else if (curFooter) curFooter.remove();
      else if (newFooter) currentDialog.appendChild(newFooter.cloneNode(true));

      [
        currentDialog.querySelector('[data-cart-body]'),
        currentDialog.querySelector('[data-cart-footer]'),
      ]
        .filter(Boolean)
        .forEach((el) => {
          el.style.transition = 'none';
          el.style.opacity = '0';
          void el.offsetWidth;
          el.style.transition = 'opacity 0.25s ease';
          el.style.opacity = '1';
          setTimeout(() => {
            el.style.transition = '';
            el.style.opacity = '';
          }, 300);
        });

      this.#reinitSliders();
    }, 200);
  }

  // Cross-fade the upsell carousel to new content (gamme exhausted → fallback).
  #crossfadeUpsell(scroller, newHTML) {
    scroller.style.transition = 'opacity 0.25s ease';
    scroller.style.opacity = '0';
    setTimeout(() => {
      scroller.innerHTML = newHTML;
      scroller.scrollLeft = 0;
      void scroller.offsetWidth;
      scroller.style.opacity = '1';
      setTimeout(() => scroller.removeAttribute('style'), 300);
    }, 250);
  }

  // Controlled smooth scroll (easeInOutCubic; native 'smooth' is too abrupt).
  #smoothScrollTo(el, to, duration) {
    const start = el.scrollTop;
    const change = to - start;
    if (Math.abs(change) < 1) return;
    const startTime = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      el.scrollTop = start + change * ease(t);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}